'use client';

import { type ChangeEvent, type SubmitEvent, Suspense, useCallback } from 'react';
import { type CourseSummary } from '@/lib/courses';
import { type TranslationKey, useTranslation } from '@/lib/i18n';
import { CourseCard, CourseCardList } from './course-cards';
import { type CoursesController, type ViewMode, useCourses } from './use-courses';

/** Sort options rendered in the order shown to the user (data-driven). */
const SORT_OPTIONS: readonly { readonly value: string; readonly labelKey: TranslationKey }[] = [
  { value: 'popular', labelKey: 'courses.sort.popular' },
  { value: 'newest', labelKey: 'courses.sort.newest' },
  { value: 'rating', labelKey: 'courses.sort.rating' },
  { value: 'price_low', labelKey: 'courses.sort.priceLow' },
  { value: 'price_high', labelKey: 'courses.sort.priceHigh' },
] as const;

/** Centered spinner used for both the Suspense fallback and loading state. */
function LoadingState(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className='py-12 text-center'>
      <div className='mx-auto mb-4 size-8 animate-spin rounded-full border-4 border-purple-600 border-t-transparent' />
      <p className='opacity-70'>{t('common.loadingCourses')}</p>
    </div>
  );
}

/** Search box, sort selector, and grid/list toggle. */
function Toolbar(props: { readonly ctrl: CoursesController }): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl } = props;

  const onSubmit = useCallback(
    (e: SubmitEvent<HTMLFormElement>): void => {
      e.preventDefault();
      ctrl.submitSearch();
    },
    [ctrl],
  );
  const onQueryChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      ctrl.setDraftQuery(e.target.value);
    },
    [ctrl],
  );
  const onSortChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>): void => {
      ctrl.setSortBy(e.target.value);
    },
    [ctrl],
  );
  const setGrid = useCallback((): void => ctrl.setViewMode('grid'), [ctrl]);
  const setList = useCallback((): void => ctrl.setViewMode('list'), [ctrl]);

  return (
    <div className='mb-8 flex flex-col gap-4 md:flex-row'>
      <form onSubmit={onSubmit} className='relative flex-1'>
        <input
          type='text'
          value={ctrl.draftQuery}
          onChange={onQueryChange}
          placeholder={t('courses.searchPlaceholder')}
          className='w-full rounded-xl border py-3 pl-12 pr-4 focus:border-purple-500 focus:ring-2 focus:ring-purple-500'
        />
      </form>
      <div className='flex gap-4'>
        <select
          value={ctrl.sortBy}
          onChange={onSortChange}
          aria-label={t('courses.sort.popular')}
          className='appearance-none rounded-xl border bg-white px-4 py-3 focus:ring-2 focus:ring-purple-500'
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>
        <ViewToggle viewMode={ctrl.viewMode} onGrid={setGrid} onList={setList} />
      </div>
    </div>
  );
}

/** Grid/list layout switch. */
function ViewToggle(props: {
  readonly viewMode: ViewMode;
  readonly onGrid: () => void;
  readonly onList: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const base = 'rounded-lg p-2';
  const active = 'bg-white shadow-sm';
  const idle = 'hover:bg-gray-200';
  return (
    <div className='hidden items-center gap-1 rounded-xl bg-gray-100 p-1 md:flex'>
      <button
        type='button'
        onClick={props.onGrid}
        className={`${base} ${props.viewMode === 'grid' ? active : idle}`}
      >
        {t('courses.view.grid')}
      </button>
      <button
        type='button'
        onClick={props.onList}
        className={`${base} ${props.viewMode === 'list' ? active : idle}`}
      >
        {t('courses.view.list')}
      </button>
    </div>
  );
}

/** Category filter chips driven by the loaded categories. */
function CategoryFilters(props: { readonly ctrl: CoursesController }): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl } = props;
  const onAll = useCallback((): void => ctrl.selectCategory(null), [ctrl]);
  const onPick = useCallback((slug: string): void => ctrl.selectCategory(slug), [ctrl]);
  const chip = 'rounded-full px-4 py-2 text-sm font-medium transition-colors';
  return (
    <div className='mb-8 flex flex-wrap gap-2'>
      <button
        type='button'
        onClick={onAll}
        className={`${chip} ${ctrl.activeCategory ? 'bg-gray-100 hover:bg-gray-200' : 'bg-purple-600 text-white'}`}
      >
        {t('courses.allCourses')}
      </button>
      {ctrl.categories.map((cat) => (
        <CategoryChip
          key={cat.slug}
          slug={cat.slug}
          label={t('courses.categoryChip', { name: cat.name, count: cat.count })}
          selected={ctrl.activeCategory === cat.slug}
          onPick={onPick}
        />
      ))}
    </div>
  );
}

/** A single category chip; isolates the per-item click handler. */
function CategoryChip(props: {
  readonly slug: string;
  readonly label: string;
  readonly selected: boolean;
  readonly onPick: (slug: string) => void;
}): React.JSX.Element {
  const handle = useCallback((): void => props.onPick(props.slug), [props]);
  return (
    <button
      type='button'
      onClick={handle}
      className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
        props.selected ? 'bg-purple-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
      }`}
    >
      {props.label}
    </button>
  );
}

/** Grid or list of result cards. */
function CourseGrid(props: {
  readonly courses: readonly CourseSummary[];
  readonly viewMode: ViewMode;
}): React.JSX.Element {
  if (props.viewMode === 'grid') {
    return (
      <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
        {props.courses.map((course) => (
          <CourseCard key={course.id} course={course} />
        ))}
      </div>
    );
  }
  return (
    <div className='space-y-4'>
      {props.courses.map((course) => (
        <CourseCardList key={course.id} course={course} />
      ))}
    </div>
  );
}

/** Empty-state panel with a clear-filters action. */
function EmptyResults(props: { readonly onClear: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className='rounded-xl bg-gray-100 py-12 text-center'>
      <h3 className='mb-2 text-lg font-semibold'>{t('courses.none.title')}</h3>
      <p className='mb-4 opacity-70'>{t('courses.none.subtitle')}</p>
      <button
        type='button'
        onClick={props.onClear}
        className='rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700'
      >
        {t('courses.none.clear')}
      </button>
    </div>
  );
}

/** Featured-courses banner shown only on the unfiltered homepage. */
function FeaturedSection(props: {
  readonly courses: readonly CourseSummary[];
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (props.courses.length === 0) {
    return null;
  }
  return (
    <section className='mb-12'>
      <h2 className='mb-6 text-2xl font-bold'>{t('courses.featured')}</h2>
      <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
        {props.courses.map((course) => (
          <CourseCard key={course.id} course={course} featured />
        ))}
      </div>
    </section>
  );
}

/** Result count heading + grid or empty-state. */
function ResultsSection(props: { readonly ctrl: CoursesController }): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl } = props;
  const noun = ctrl.courses.length === 1 ? t('courses.noun.singular') : t('courses.noun.plural');
  return (
    <section>
      {ctrl.isHomepage ? null : (
        <h2 className='mb-4 text-xl font-bold'>
          {t('courses.found', { count: ctrl.courses.length, noun })}
        </h2>
      )}
      {ctrl.courses.length === 0 ? (
        <EmptyResults onClear={ctrl.clearFilters} />
      ) : (
        <CourseGrid courses={ctrl.courses} viewMode={ctrl.viewMode} />
      )}
    </section>
  );
}

/** Featured + result sections (only rendered once loading completes). */
function Results(props: { readonly ctrl: CoursesController }): React.JSX.Element {
  const { ctrl } = props;
  return (
    <>
      {ctrl.isHomepage ? <FeaturedSection courses={ctrl.featuredCourses} /> : null}
      <ResultsSection ctrl={ctrl} />
    </>
  );
}

function CoursesContent(): React.JSX.Element {
  const { t } = useTranslation();
  const ctrl = useCourses();
  return (
    <div className='mx-auto max-w-7xl px-4 py-8'>
      <div className='mb-8'>
        <h1 className='mb-2 text-3xl font-bold'>{t('courses.title')}</h1>
        <p className='opacity-70'>{t('courses.subtitle')}</p>
      </div>
      <Toolbar ctrl={ctrl} />
      <CategoryFilters ctrl={ctrl} />
      {ctrl.loading ? <LoadingState /> : <Results ctrl={ctrl} />}
    </div>
  );
}

export default function CoursesPage(): React.JSX.Element {
  return (
    <Suspense
      fallback={
        <div className='mx-auto max-w-7xl px-4 py-12'>
          <LoadingState />
        </div>
      }
    >
      <CoursesContent />
    </Suspense>
  );
}
